'use client';

import clsx from 'clsx';
import NextImage from 'next/image';
import React from 'react';

import { useReducedMotion } from '@/hooks/useReducedMotion';

type NextImageProps = {
  useSkeleton?: boolean;
  imgClassName?: string;
  blurClassName?: string;
  alt: string;
  width: string | number;
  height: string | number;
  priority?: boolean;
} & React.ComponentProps<typeof NextImage>;

/**
 *
 * @description Must set width using `w-` className
 * @param useSkeleton add background with pulse animation, don't use it if image is transparent
 */
export default function Image({
  useSkeleton = false,
  imgClassName,
  blurClassName,
  alt,
  width,
  height,
  priority = false,
  ...props
}: NextImageProps) {
  const [loading, setLoading] = React.useState(useSkeleton);
  const prefersReducedMotion = useReducedMotion();

  // Improved loading state
  const [imageLoaded, setImageLoaded] = React.useState(false);

  const handleLoad = React.useCallback(() => {
    setLoading(false);
    setImageLoaded(true);
  }, []);

  return (
    <div className={clsx('overflow-hidden', imgClassName)}>
      {/* Loading placeholder for better UX */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse rounded-xl" />
      )}
      <NextImage
        className={clsx(
          'duration-500 ease-in-out',
          loading || !imageLoaded
            ? 'scale-105 blur-sm opacity-0'
            : 'scale-100 blur-0 opacity-100',
          prefersReducedMotion ? '' : 'transition-all',
          blurClassName
        )}
        onLoad={handleLoad}
        width={width}
        height={height}
        priority={priority}
        alt={alt}
        placeholder="blur"
        blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
        {...props}
      />
    </div>
  );
}
